import { RefObject } from 'react';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { InputTextProps } from '@/trio-ui/InputText';
import { cleanStringSpaces } from '@/utils/global';
import * as Styles from './AssignedUsers.styles';
import AssignableItem from '../AssignableUser';
import { UserType } from '../UserSuggestions/UserSuggestions.component';

export type AssignesUsersComponentProps = Pick<
  InputTextProps,
  'label' | 'value' | 'onChangeHandler' | 'placeholder'
> & {
  assignedUsers: UserType[];
  isUserReviewer?: boolean;
  onAssignToMyself?: () => void;
  onRemoveAssignedUser: (id: string) => void;
  searchResults: UserType[];
  onSelectItem: (id: string) => void;
  disableRemoveButton?: string;
  inputRef: RefObject<HTMLInputElement>;
  resultDropdownRef: RefObject<HTMLDivElement>;
  isDropdownVisible: boolean;
  shouldShowRemoveButton?: boolean;
};

export const inputId = 'assigned-users-input';
export const resultDropdownId = 'assigned-users-result-dropdown';

export default function AssignedUsersComponent({
  shouldShowRemoveButton,
  assignedUsers,
  isUserReviewer,
  onAssignToMyself,
  onRemoveAssignedUser,
  value,
  onChangeHandler,
  searchResults,
  onSelectItem,
  label = 'Assignees',
  placeholder,
  disableRemoveButton,
  inputRef,
  resultDropdownRef,
  isDropdownVisible,
  ...props
}: AssignesUsersComponentProps): JSX.Element {
  return (
    <Styles.Box {...props}>
      <Styles.SearchArea>
        <Styles.InputLabel data-testid="assigned-users-subtitle">{label}</Styles.InputLabel>
        <Styles.Input
          testId="assigned-users-input-search"
          name="suggestion"
          value={value}
          onChangeHandler={onChangeHandler}
          size="sm"
          placeholder={placeholder}
          ref={inputRef}
          id={inputId}
        />
        {value && isDropdownVisible && (
          <Styles.SearchResults
            data-testid="assigned-users-results"
            ref={resultDropdownRef}
            id={resultDropdownId}
          >
            <ul>
              {searchResults.map((result, index) => {
                return (
                  <Styles.SearchResultItem
                    data-testid={`assigned-users-${cleanStringSpaces(result.firstName)}`}
                    key={index.toString()}
                  >
                    <button
                      onClick={() => onSelectItem(result.id)}
                      aria-label={`Assign to ${result.firstName}`}
                      type="button"
                    >
                      <AssignableItem
                        id={result.id}
                        pictureUrl={result.pictureUrl ?? ''}
                        firstName={result.firstName}
                        lastName={result.lastName}
                      />
                    </button>
                  </Styles.SearchResultItem>
                );
              })}
            </ul>
            {!searchResults.length && value && (
              <Styles.NotFoundResults data-testid="assigned-users-not-found">
                No candidate found
              </Styles.NotFoundResults>
            )}
          </Styles.SearchResults>
        )}
      </Styles.SearchArea>
      {isUserReviewer && onAssignToMyself && (
        <Styles.AssignToMyselft onClick={onAssignToMyself} isBlueButton>
          <>
            <AssignmentTurnedInOutlinedIcon />
            Assign to myself
          </>
        </Styles.AssignToMyselft>
      )}
      {!!assignedUsers?.length && (
        <div>
          <ul>
            {assignedUsers.map((suggestion, index) => {
              return (
                <Styles.AssignedUsersItem key={index.toString()}>
                  <AssignableItem
                    id={suggestion.id}
                    pictureUrl={suggestion?.pictureUrl ?? ''}
                    onRemove={shouldShowRemoveButton ? onRemoveAssignedUser : undefined}
                    firstName={suggestion.firstName}
                    lastName={suggestion.lastName}
                    disableRemoveButton={disableRemoveButton === suggestion.id}
                  />
                </Styles.AssignedUsersItem>
              );
            })}
          </ul>
        </div>
      )}
    </Styles.Box>
  );
}
